import React, { useCallback, useEffect, useMemo, useState } from 'react';
import * as Sentry from '@sentry/react';
import { PetSelectorList } from './components/PetSelectorView/PetSelectorList';
import {
  BalanceForClientFragment,
  CareSubscriptionFragment,
  CareSubscriptionPaymentStatus,
  InvoiceForClientFragment,
  InvoiceStatus,
  SidePanelParentPetDataFragment,
  StripePaymentIntentStatus,
  useGetBalanceForClientLazyQuery,
  useGetCustomerStripePortalUrlLazyQuery,
  useGetDirectedCareLoginLinkMutation,
  useGetInvoicesForClientLazyQuery,
  useGetSidePanelBenefitsClinicPetParentQuery,
} from 'shared/types/graphql';
import { usePets } from './hooks/usePets';
import { usePetBenefits } from './hooks/usePetBenefits';
import useFeatureFlag from 'shared/hooks/useFeatureFlag';
import { FeatureFlagName } from 'shared/enums/FeatureFlagName';
import { useSidePanelSearch } from '../../state/providers/SearchPanelProvider';
import {
  Icon,
  Text,
  Button,
  Center,
  useDisclosure,
  Box,
  Divider,
  Flex,
  VStack,
  Spinner,
  Alert,
  useToast,
  Heading,
} from '@televet/kibble-ui';
import CareWizardModal from './components/CareWizardModal';
import { useCareBenefitFollowup } from './hooks/useCareBenefitFollowup';
import FollowupStepsForm from './components/FollowupStepsForm';
import { CareMembershipEvents } from 'shared/providers/TrackingProvider/EventNames';
import { StripeTerminalClinicPetParent } from 'shared/providers/StripeTerminalProvider/types/StripeTerminalClinicPetParent';
import { GraphQLErrorPolicies, GraphQLFetchPolicies, PersistedStateKeys } from 'shared/enums';
import { useIntegrationsProvider } from 'shared/providers/IntegrationsProvider';
import usePersistedState from 'shared/hooks/usePersistedState';
import usePaymentWritebackConfig from 'shared/hooks/usePaymentWritebackConfig';
import ManageMembershipModal from './components/ManageMembership/ManageMembershipModal';
import ManageMembershipProvider from './components/ManageMembership/context/ManageMembershipContext';
import formatDate from 'date-fns/format';
import { centsToDollars, formatCurrency } from './utils/currency';
import { useTrueUpInfo } from './hooks/useTrueUpInfo';
import { isEnrollmentConsideredActive } from './utils/careEnrollments';
import { CareActivityLog } from './components/ActivityLog/CareActivityLog';
import { RemainingBenefitRow } from './components/RemainingBenefitRow';
import { getCardBrandIcon, getIsExpired } from './utils/paymentMethod';
import { useCareActivityLog } from './hooks/useCareActivityLog';
import { CareEnrollmentPaymentData, CareEnrollmentPaymentDataType } from './components/ActivityLog/types';
import { MembershipPastDueAlert } from './components/MembershipPastDueAlert';
import { MembershipDisputeAlert } from './components/MembershipDisputeAlert';
import { useTracking } from 'shared/providers/TrackingProvider';

interface ISidePanelBenefitsProps {
  clinicPetParentId?: string | null;
}

const SidePanelBenefits = ({ clinicPetParentId }: ISidePanelBenefitsProps): JSX.Element => {
  const { isInvoiceDiscoverySupported, isInvoicePreviewSupported } = useIntegrationsProvider();
  const [isDiscoveredInvoiceDemoModeEnabled] = usePersistedState(
    PersistedStateKeys.IsDiscoveredInvoiceDemoModeEnabled,
    false,
  );
  const [emptyInvoiceStateMessage, setEmptyInvoiceStateMessage] = useState('');
  const [careLink, setCareLink] = useState<string | null | undefined>('');
  const {
    openBenefitsModal,
    refreshUsages: refreshUsagesState,
    setRefreshUsages,
    setOpenBenefitsModal,
    setCurrentClinicPetId,
    currentClinicPetId,
  } = useSidePanelSearch();
  const { data: clinicPetParentData, loading: isClinicPetParentDataLoading } =
    useGetSidePanelBenefitsClinicPetParentQuery({
      variables: { clinicPetParentId: clinicPetParentId || '' },
      skip: !clinicPetParentId,
    });
  const { pets, selectedPet, select, refetchPets } = usePets(
    clinicPetParentData?.findUniqueClinicPetParent?.id,
    currentClinicPetId,
  );
  const {
    benefits,
    loading: loadingBenefits,
    refresh: refreshBenefits,
  } = usePetBenefits(selectedPet?.organizationPet ? selectedPet.organizationPet.id : '');

  const { items: activityLogItems } = useCareActivityLog({
    organizationPetId: selectedPet?.organizationPet?.id,
    organizationPetParentId: clinicPetParentData?.findUniqueClinicPetParent?.organizationPetParent?.id,
  });

  const { isFeatureEnabled } = useFeatureFlag();
  const toast = useToast();
  const isCareEnabled = isFeatureEnabled(FeatureFlagName.CarePlans);
  const isWizardPaymentsFFEnabled = isFeatureEnabled(FeatureFlagName.WizardPaymentIntegrations);
  const isPimsClinicPetParent = useMemo(() => {
    return !!clinicPetParentData?.findUniqueClinicPetParent?.pimsId;
  }, [clinicPetParentData?.findUniqueClinicPetParent?.pimsId]);
  const { followup: incompleteFollowup } = useCareBenefitFollowup(selectedPet?.organizationPet?.id);
  const { isClinicWritebackEnabled } = usePaymentWritebackConfig();
  const { trackEvent } = useTracking();

  const incompleteFollowupSteps = useMemo(
    () => (!!incompleteFollowup?.followupSteps ? incompleteFollowup?.followupSteps : []),
    [incompleteFollowup?.followupSteps],
  );

  const showFallBackDispay = useMemo(() => {
    return !!isWizardPaymentsFFEnabled && !!isClinicWritebackEnabled && !!isPimsClinicPetParent;
  }, [isWizardPaymentsFFEnabled, isClinicWritebackEnabled, isPimsClinicPetParent]);

  const pastDueInfo = useMemo(() => {
    const pastDuePayments = (activityLogItems
      ?.map((item) => item.data)
      .filter((data) => data.type === 'CareEnrollmentPayment' && !data.paidAt) || []) as CareEnrollmentPaymentData[];
    return {
      totalAmountDue: pastDuePayments.reduce((acc, item) => acc + item.amount, 0),
      totalPaymentsDue: pastDuePayments.length,
    };
  }, [activityLogItems]);

  const disputes = useMemo(() => {
    return activityLogItems
      ?.map((item) => item.data)
      .filter(
        (data) => data.type === 'CareEnrollmentPayment' && data.subType === CareEnrollmentPaymentDataType.Dispute,
      ) as CareEnrollmentPaymentData[];
  }, [activityLogItems]);

  const {
    isOpen: isCareWizardOpen,
    onOpen: onCareWizardOpen,
    onClose: onCareWizardClose,
  } = useDisclosure({ defaultIsOpen: openBenefitsModal });

  const {
    isOpen: isManageMembershipModalOpen,
    onOpen: onManageMembershipModalOpen,
    onClose: onManageMembershipModalClose,
  } = useDisclosure();

  const currentEnrollment = useMemo(
    () =>
      selectedPet?.organizationPet?.carePlanEnrollments?.find((enrollment) => isEnrollmentConsideredActive(enrollment)),
    [selectedPet?.organizationPet?.carePlanEnrollments],
  );

  const { trueUpData, refetchTrueUpData } = useTrueUpInfo({ carePlanEnrollmentId: currentEnrollment?.id });

  const refetchPetData = useCallback((): void => {
    refetchPets();
    refreshBenefits();
    refetchTrueUpData();
  }, [refetchPets, refetchTrueUpData, refreshBenefits]);

  const [getCareUrlLink] = useGetDirectedCareLoginLinkMutation({
    variables: {
      data: {
        clinicPetParentId: clinicPetParentId || '',
        clinicPetId: selectedPet?.id,
        organizationPlanId: currentEnrollment?.planId || '',
      },
    },
  });

  const onCopyToClipboard = (linkValue: string): void => {
    navigator.clipboard.writeText(linkValue);
    toast({
      title: 'Copied to clipboard',
      duration: 2000,
    });
  };

  const handleCopyToClipboard = async (): Promise<void> => {
    trackEvent(CareMembershipEvents.PetPortalLinkCtaClick, {
      utm_source: 'membership tab',
      utm_medium: 'flow',
    });
    try {
      if (!!careLink) {
        onCopyToClipboard(careLink);
      } else {
        const { data } = await getCareUrlLink();
        setCareLink(data?.getDirectedCareLoginLink.link);
        if (data) {
          onCopyToClipboard(data?.getDirectedCareLoginLink.link);
        }
      }
    } catch (error) {
      toast({
        status: 'error',
        title: 'Something went wrong!',
        duration: 2000,
      });
    }
  };
  const { firstName, lastName } = clinicPetParentData?.findUniqueClinicPetParent || {};

  const fullName = [firstName, lastName].join(' ').trim() || 'this pet parent';

  useEffect(() => {
    // ensure selected pet and persisted pet are in sync so that navigating to pet profile has the correct pet selected
    if (!!selectedPet?.id && selectedPet?.id !== currentClinicPetId) {
      setCurrentClinicPetId(selectedPet?.id);
    }
  }, [selectedPet, currentClinicPetId, setCurrentClinicPetId]);

  useEffect(() => {
    if (refreshUsagesState) {
      setRefreshUsages(false);
    }
  }, [refreshUsagesState, setRefreshUsages]);

  const handleCareWizardClose = useCallback(() => {
    onCareWizardClose();
    refreshBenefits();
    if (openBenefitsModal) {
      setOpenBenefitsModal(false);
    }
  }, [onCareWizardClose, openBenefitsModal, refreshBenefits, setOpenBenefitsModal]);

  const stripeCustomerId = useMemo(
    () => clinicPetParentData?.findUniqueClinicPetParent?.organizationPetParent?.stripeCustomerTableId,
    [clinicPetParentData?.findUniqueClinicPetParent?.organizationPetParent?.stripeCustomerTableId],
  );

  const invoiceClinicPetParent: StripeTerminalClinicPetParent | undefined = useMemo(
    () =>
      clinicPetParentData?.findUniqueClinicPetParent
        ? {
            id: clinicPetParentData?.findUniqueClinicPetParent?.id,
            firstName: clinicPetParentData?.findUniqueClinicPetParent?.firstName,
            lastName: clinicPetParentData?.findUniqueClinicPetParent?.lastName,
            email: clinicPetParentData?.findUniqueClinicPetParent?.email,
            pimsId: clinicPetParentData?.findUniqueClinicPetParent?.pimsId,
            organizationPetParentId: clinicPetParentData.findUniqueClinicPetParent.organizationPetParent?.id,
          }
        : undefined,
    [clinicPetParentData?.findUniqueClinicPetParent],
  );

  const [getInvoices, { data: invoiceData }] = useGetInvoicesForClientLazyQuery({
    fetchPolicy: GraphQLFetchPolicies.NetworkOnly,
    errorPolicy: GraphQLErrorPolicies.All,
    onError: (error) => {
      console.error(error);
      Sentry.captureException(error);
      setEmptyInvoiceStateMessage('Error connecting to invoice data, please wait a moment and try again');
    },
    onCompleted: () => {
      setEmptyInvoiceStateMessage('There are no pending invoices');
    },
  });

  const [getBalance, { data: balanceData }] = useGetBalanceForClientLazyQuery({
    fetchPolicy: GraphQLFetchPolicies.NetworkOnly,
    errorPolicy: GraphQLErrorPolicies.All,
  });

  const fetchInvoices = useCallback(() => {
    // If we can't show invoices, don't fetch them (wasteful query to Bitwerx)
    const shouldFetchInvoices =
      !!clinicPetParentId && !!isPimsClinicPetParent && !!isInvoiceDiscoverySupported && !!isInvoicePreviewSupported;

    if (!shouldFetchInvoices) {
      return;
    }

    if (isDiscoveredInvoiceDemoModeEnabled) {
      return;
    }

    getInvoices({
      variables: {
        where: {
          clinicPetParentId: { equals: clinicPetParentId },
          status: { equals: InvoiceStatus.Open },
          total: { gt: 0 },
          stripePaymentIntents: { none: { status: { equals: StripePaymentIntentStatus.Succeeded } } },
        },
      },
    });
  }, [
    clinicPetParentId,
    isPimsClinicPetParent,
    isInvoiceDiscoverySupported,
    isInvoicePreviewSupported,
    isDiscoveredInvoiceDemoModeEnabled,
    getInvoices,
  ]);

  const fetchBalance = useCallback(() => {
    if (isDiscoveredInvoiceDemoModeEnabled) {
      return;
    }

    if (clinicPetParentId && !!isPimsClinicPetParent) {
      getBalance({
        variables: {
          where: {
            clinicPetParentId: clinicPetParentId,
          },
        },
      });
    }
  }, [clinicPetParentId, getBalance, isDiscoveredInvoiceDemoModeEnabled, isPimsClinicPetParent]);

  useEffect(() => {
    if (!!invoiceClinicPetParent?.pimsId && !!invoiceClinicPetParent?.id && isInvoiceDiscoverySupported) {
      fetchInvoices();
      fetchBalance();
    }
  }, [
    fetchBalance,
    fetchInvoices,
    invoiceClinicPetParent?.id,
    invoiceClinicPetParent?.pimsId,
    isInvoiceDiscoverySupported,
  ]);

  const invoices: InvoiceForClientFragment[] = useMemo(() => {
    return invoiceData?.getInvoicesForClient || [];
  }, [invoiceData?.getInvoicesForClient]);

  const balance: BalanceForClientFragment = useMemo(() => {
    return balanceData?.getBalanceForClient || {};
  }, [balanceData?.getBalanceForClient]);

  const invoiceId = useMemo(() => {
    return invoices.length > 0 ? invoices[0].id : '';
  }, [invoices]);

  const selectedPetSubscription: CareSubscriptionFragment | undefined = useMemo(() => {
    return selectedPet?.organizationPet?.associatedSubscriptions.find(
      (subscription) => subscription.id === currentEnrollment?.careStripeSubscriptionId,
    );
  }, [currentEnrollment?.careStripeSubscriptionId, selectedPet?.organizationPet?.associatedSubscriptions]);

  const careMembershipPaymentStatus = useMemo(
    () => selectedPetSubscription?.paymentStatus,
    [selectedPetSubscription?.paymentStatus],
  );

  const [getStripePortalUrl, { loading: stripePortalUrlLoading }] = useGetCustomerStripePortalUrlLazyQuery();

  const openBillingPortal = useCallback(async (): Promise<void> => {
    try {
      const { data } = await getStripePortalUrl({
        variables: {
          data: { returnUrl: window.location.href },
          clinicPetParentId,
        },
      });

      if (data?.stripeRetrievePetParentPortalUrl?.url) {
        window.open(data?.stripeRetrievePetParentPortalUrl?.url, '_blank')?.focus();
      } else {
        toast({
          title: 'Failed to open Billing portal. Please try again.',
          status: 'error',
          duration: 2000,
        });
      }
    } catch (error) {
      toast({
        title: 'Failed to open Billing portal. Please try again.',
        status: 'error',
        duration: 2000,
      });
      console.error(error);
    }
  }, [clinicPetParentId, getStripePortalUrl, toast]);

  const subscriptionPaymentMethod = useMemo(() => {
    const paymentMethod = selectedPetSubscription?.stripePaymentMethod;

    if (!paymentMethod) {
      return <></>;
    }

    const isCardExpired =
      paymentMethod?.isExpired || getIsExpired(paymentMethod?.expirationMonth, paymentMethod?.expirationYear);
    const isPastDue = selectedPetSubscription?.paymentStatus === CareSubscriptionPaymentStatus.PastDue;

    return (
      <>
        <Flex alignItems="center" justifyContent="space-between">
          <Flex alignItems="center">
            {getCardBrandIcon(`${paymentMethod?.cardBrand}`, 'md')}
            <Text as="p" mx={1} variant={isCardExpired || isPastDue ? 'danger' : 'subtle'} size="sm">{`**** **** **** ${
              paymentMethod?.last4 || ''
            }`}</Text>
          </Flex>
          <Text variant={isCardExpired || isPastDue ? 'danger' : 'subtle'} size="sm">
            {paymentMethod?.expirationMonth}/{paymentMethod?.expirationYear}
          </Text>
        </Flex>
        {isPastDue && (
          <Flex mt={1}>
            <Icon name="warningSign" size={'sm'} variant="danger" />
            <Text as="p" variant="danger" size="sm" mx={1}>
              Update billing information and pay total amount due of{' '}
              <Text fontWeight="bold" variant="danger" size="sm">
                {formatCurrency(centsToDollars(pastDueInfo.totalAmountDue), true)}
              </Text>
              .
            </Text>
          </Flex>
        )}
        <Button
          size="sm"
          variant="secondary"
          iconName="externalLink"
          onClick={openBillingPortal}
          isLoading={stripePortalUrlLoading}
          w="100%"
          mt={2}
        >
          Edit Billing Information
        </Button>
      </>
    );
  }, [
    openBillingPortal,
    pastDueInfo.totalAmountDue,
    selectedPetSubscription?.paymentStatus,
    selectedPetSubscription?.stripePaymentMethod,
    stripePortalUrlLoading,
  ]);

  return !isClinicPetParentDataLoading ? (
    <Flex direction="column" overflowY="auto" bgColor="background.subtle" height="-webkit-fill-available">
      <Box p={4} pt={2} borderBottom="1px" borderColor="border.default" bgColor="background.default">
        <Text as="p" mb={4} size="xl" fontWeight="bold">
          Manage Benefits for...
        </Text>
        {selectedPet ? (
          <PetSelectorList
            allPets={pets}
            selectedPet={selectedPet}
            selectPet={(pet: SidePanelParentPetDataFragment): void => {
              select(pet.id);
              setCareLink('');
            }}
          />
        ) : (
          pets.length === 0 && <Box textAlign="center">None of {fullName}&apos;s pets have been enrolled in Care</Box>
        )}
        <Flex alignItems="center" justifyContent="space-between" w="100%" mt={4}>
          <Text size="sm" fontWeight="bold">
            Pet Portal Link
          </Text>
          <Button onClick={(): Promise<void> => handleCopyToClipboard()} size="xs" variant="secondarySubtle">
            Copy Link
          </Button>
        </Flex>
      </Box>
      {!trueUpData ? (
        <>
          <Box bgColor="background.default">
            {!!currentEnrollment && (
              <Box px={4} pt={4}>
                <Heading size="md">{currentEnrollment.plan.flowAlias || currentEnrollment.plan.title}</Heading>
                <MembershipPastDueAlert
                  status={careMembershipPaymentStatus}
                  pastDueInfo={pastDueInfo}
                  openBillingPortal={openBillingPortal}
                />
                <MembershipDisputeAlert disputes={disputes} />
                <Box py={3}>
                  <Text fontWeight="semibold">Remaining Benefits</Text>
                  <Divider color="border.default" my={2} />
                  {loadingBenefits ? (
                    <Flex alignItems="center" justifyContent="center">
                      <Spinner />
                    </Flex>
                  ) : (
                    <>
                      {benefits.map((benefit) => (
                        <RemainingBenefitRow
                          key={benefit.id}
                          benefit={benefit}
                          enrollmentExpirationDate={currentEnrollment.expirationDate}
                        />
                      ))}
                    </>
                  )}
                </Box>
                {isCareEnabled && benefits.length > 0 && (
                  <>
                    <Center pb={3}>
                      <VStack spacing={2} w="100%">
                        {incompleteFollowupSteps?.length > 0 ? (
                          <VStack>
                            <FollowupStepsForm
                              pet={selectedPet}
                              clinicPetParent={invoiceClinicPetParent}
                              showCompletedSteps={showFallBackDispay}
                              forceFilledForm={true}
                              buttonProps={{ size: 'sm', isFullWidth: true }}
                              showFallBackDisplay={showFallBackDispay}
                              invoiceId={invoiceId}
                              refreshInvoice={fetchInvoices}
                              refreshBalance={fetchBalance}
                            />
                          </VStack>
                        ) : (
                          <Button
                            data-testid="calculate-benefits-button"
                            size="sm"
                            isFullWidth={true}
                            onClick={(): void => {
                              trackEvent(CareMembershipEvents.WizardStart);
                              onCareWizardOpen();
                            }}
                            isDisabled={loadingBenefits}
                          >
                            Calculate Benefits
                          </Button>
                        )}
                        <Button
                          data-testid="manage-membership-button"
                          size="sm"
                          isFullWidth={true}
                          onClick={(): void => {
                            trackEvent(CareMembershipEvents.ManageStart);
                            onManageMembershipModalOpen();
                          }}
                          isDisabled={loadingBenefits}
                          iconPosition="left"
                          iconName="wrench"
                          variant="secondary"
                        >
                          Manage Membership
                        </Button>
                      </VStack>
                    </Center>
                    {isCareWizardOpen && (
                      <CareWizardModal
                        isOpen={isCareWizardOpen}
                        onClose={handleCareWizardClose}
                        pet={selectedPet}
                        careStripeSubscriptionId={currentEnrollment?.careStripeSubscriptionId || ''}
                        clinicPetParent={invoiceClinicPetParent}
                        propInvoiceData={invoices}
                        propBalanceData={balance}
                        emptyInvoiceMessage={emptyInvoiceStateMessage}
                        refreshInvoice={fetchInvoices}
                        refreshBalance={fetchBalance}
                        pastDueInfo={pastDueInfo}
                        status={careMembershipPaymentStatus}
                        openBillingPortal={openBillingPortal}
                      />
                    )}
                    {isManageMembershipModalOpen && (
                      <ManageMembershipProvider>
                        <ManageMembershipModal
                          isOpen={isManageMembershipModalOpen}
                          onClose={onManageMembershipModalClose}
                          pet={selectedPet}
                          clinicPetParent={invoiceClinicPetParent}
                          enrollment={currentEnrollment}
                          subscription={selectedPetSubscription}
                          stripeCustomerId={stripeCustomerId}
                          pets={pets}
                          refetchPetData={refetchPetData}
                          onSelectPet={(petId): void => select(petId)}
                        />
                      </ManageMembershipProvider>
                    )}
                  </>
                )}
                <Box pb={4}>
                  <Text fontWeight="semibold">Billing & Payment</Text>
                  <Divider color="border.default" my={2} />
                  <Box w="100%" mb={1}>
                    {selectedPetSubscription && (
                      <Flex alignItems="center" justifyContent="space-between">
                        <Flex alignItems="center">
                          <Icon name="calendarDate" size="sm" />
                          <Text pl={1} size="sm">
                            Next Billing Date:
                          </Text>
                        </Flex>
                        <Text size="sm">
                          {formatDate(new Date(selectedPetSubscription.nextBillingDate), 'MM/dd/yy')}
                        </Text>
                      </Flex>
                    )}
                  </Box>
                  <Flex alignItems="center" justifyContent="space-between" mb={1}>
                    <Flex alignItems="center" justifyContent="center">
                      <Icon
                        name={currentEnrollment?.autoRenews ? 'arrowCircle' : 'cancel'}
                        size="sm"
                        variant={currentEnrollment?.autoRenews ? 'success' : 'danger'}
                      />
                      <Text pl={1} variant={currentEnrollment?.autoRenews ? 'success' : 'danger'} size="sm">
                        {`Plan ${currentEnrollment?.autoRenews ? 'Renews' : 'Ends'} On:`}
                      </Text>
                    </Flex>
                    <Text variant={currentEnrollment?.autoRenews ? 'success' : 'danger'} size="sm">
                      {formatDate(new Date(currentEnrollment?.expirationDate), 'MM/dd/yy')}
                    </Text>
                  </Flex>
                  <Box>{subscriptionPaymentMethod}</Box>
                </Box>
              </Box>
            )}
          </Box>
          <Divider color="border.default" width="100%" />
        </>
      ) : (
        <Box p={4} borderBottom="1px" borderColor="border.default" bgColor="background.default">
          <Alert
            title="This Care Membership has been cancelled but payments are still being made on the remaining balance. Once the balance has been paid, this pet can be re-enrolled in Care."
            hideCloseButton
            status="info"
          />
          <Box my={4}>
            {!!trueUpData.nextBillingDate.length && (
              <Flex alignItems="center" justifyContent="space-between" mb={1}>
                <Flex alignItems="center">
                  <Icon size="md" name="calendarDate" variant="subtle" />
                  <Text as="p" variant="subtle" ml={1}>
                    Next Billing Date:
                  </Text>
                </Flex>
                <Text variant="subtle">{formatDate(new Date(trueUpData.nextBillingDate), 'MM/dd/yy')}</Text>
              </Flex>
            )}
            {!!trueUpData.finalPaymentDate.length && (
              <Flex alignItems="center" justifyContent="space-between" mb={1}>
                <Flex alignItems="center">
                  <Icon size="md" name="calendarCheck" variant="subtle" />
                  <Text as="p" variant="subtle" ml={1}>
                    Final Payment Date:
                  </Text>
                </Flex>
                <Text variant="subtle">{formatDate(new Date(trueUpData.finalPaymentDate), 'MM/dd/yy')}</Text>
              </Flex>
            )}
            <Flex alignItems="center" justifyContent="space-between" mb={1}>
              <Flex alignItems="center">
                <Icon size="md" name="dollarCircle" variant="subtle" />
                <Text as="p" variant="subtle" ml={1}>
                  Remaining Balance:
                </Text>
              </Flex>
              <Text variant="subtle">{formatCurrency(trueUpData.remainingBalance, true)}</Text>
            </Flex>
          </Box>
          {trueUpData.isRetrying && (
            <Flex alignItems="center" justifyContent="center" mb={2}>
              <Icon size="md" name="warningSign" variant="danger" />
              <Text as="p" variant="danger" ml={1}>
                Payment Failed
              </Text>
            </Flex>
          )}
          <Button
            iconName="pen"
            variant={trueUpData.isRetrying ? 'secondaryDestructive' : 'secondary'}
            size="sm"
            isFullWidth
            onClick={openBillingPortal}
            isLoading={stripePortalUrlLoading}
          >
            Edit Payment Info
          </Button>
        </Box>
      )}
      <CareActivityLog
        selectedPet={selectedPet}
        organizationPetParentId={clinicPetParentData?.findUniqueClinicPetParent?.organizationPetParent?.id}
        enrollmentId={currentEnrollment?.id}
      />
    </Flex>
  ) : (
    <Flex w="100%" h="100%" justify="center" align="center">
      <Spinner />
    </Flex>
  );
};

export default SidePanelBenefits;
